import { useState } from 'react'
import { createFileRoute, Outlet, stripSearchParams, useNavigate } from '@tanstack/react-router'
import * as valibot from 'valibot'
import {
  DEFAULT_METRICS_PRESET,
  METRICS_MAX_SPAN_MS,
  METRICS_MIN_SPAN_MS,
  METRICS_PRESET_MS,
  METRICS_PRESETS,
  METRICS_RANGE_OPTIONS,
  windowInputOf,
  type MetricsSearch,
} from 'shared/metrics-window'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { SEGMENTED_TAB, SegmentedControl, SegmentedTab } from '@/components/custom/segmented-control'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { SERIES_VARS } from './-components/admin/metrics-charts'
import { formatSpan, formatWindow, fromUtcInput, toUtcInput } from './-components/admin/metrics-format'
import { METRICS_RANGE_LABELS, MetricsPending } from './-components/admin/metrics-pending'

// The metrics: one screen per question (overview, endpoints, errors, vitals, assistant, platform), each its own route
// under this one, which owns the window they share. The window is search params, so switching screens keeps it and a
// view stays linkable. The queries: docs/notes/server/metrics-dashboard.md

const SEARCH_DEFAULTS = { range: DEFAULT_METRICS_PRESET }

export const Route = createFileRoute('/_app/admin/metrics')({
  validateSearch: valibot.pipe(
    valibot.object({
      range: valibot.fallback(
        valibot.optional(valibot.picklist(METRICS_RANGE_OPTIONS), DEFAULT_METRICS_PRESET),
        DEFAULT_METRICS_PRESET,
      ),
      from: valibot.fallback(valibot.optional(valibot.pipe(valibot.number(), valibot.integer())), undefined),
      to: valibot.fallback(valibot.optional(valibot.pipe(valibot.number(), valibot.integer())), undefined),
    }),
    // A hand-edited custom window the server would refuse reads as the default, not an error page.
    valibot.transform((search): MetricsSearch => {
      const window = windowInputOf(search)
      return 'range' in window ? { range: window.range, from: undefined, to: undefined } : search
    }),
  ),
  search: { middlewares: [stripSearchParams(SEARCH_DEFAULTS)] },
  component: RouteComponent,
  pendingComponent: MetricsPending,
  staticData: { fullWidth: true },
})

function RouteComponent() {
  return (
    <div className={cn(SETTINGS_STACK, SERIES_VARS)}>
      <Toolbar />
      <Outlet />
    </div>
  )
}

// MARK: Toolbar

function Toolbar() {
  const search = Route.useSearch()
  return (
    <div className="flex flex-wrap items-center gap-3">
      <nav aria-label="Window" className="max-w-full">
        <SegmentedControl className="max-w-full scrollbar-none overflow-auto">
          {METRICS_PRESETS.map((range) => (
            <SegmentedTab
              key={range}
              // The screen on display, with only the window changed.
              to="."
              search={{ range }}
              active={search.range === range}
              replace
              resetScroll={false}
              className="whitespace-nowrap tabular-nums"
            >
              {METRICS_RANGE_LABELS[range]}
            </SegmentedTab>
          ))}
          <CustomRange active={search.range === 'custom'} />
        </SegmentedControl>
      </nav>
      <WindowLabel />
    </div>
  )
}

function WindowLabel() {
  const window = windowInputOf(Route.useSearch())
  // Only the year is read from it, which the server's render and the browser's share but at midnight on New Year's Eve.
  const [now] = useState(() => Date.now())
  const label =
    'range' in window
      ? `Last ${formatSpan(METRICS_PRESET_MS[window.range])}`
      : formatWindow(window.from, window.to, now)
  const span = 'range' in window ? METRICS_PRESET_MS[window.range] : window.to - window.from
  return (
    <span className="text-muted-foreground text-xs tabular-nums">
      {label} · compared with the {formatSpan(span)} before
    </span>
  )
}

function CustomRange(props: { active: boolean }) {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        setError(null)
      }}
    >
      <PopoverTrigger
        className={cn(SEGMENTED_TAB, 'whitespace-nowrap')}
        aria-current={props.active ? 'page' : undefined}
      >
        {METRICS_RANGE_LABELS.custom}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <PopoverHeader>
          <PopoverTitle>Custom window</PopoverTitle>
          <PopoverDescription>In UTC, from five minutes to 90 days.</PopoverDescription>
        </PopoverHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            const form = new FormData(event.currentTarget)
            const field = (name: string) => {
              const value = form.get(name)
              return typeof value === 'string' ? fromUtcInput(value) : null
            }
            const from = field('from')
            const to = field('to')
            if (from === null || to === null) return setError('Pick both ends.')
            if (to - from < METRICS_MIN_SPAN_MS)
              return setError('The end must be at least five minutes after the start.')
            if (to - from > METRICS_MAX_SPAN_MS) return setError('A window spans at most 90 days.')
            setOpen(false)
            void navigate({ to: '.', search: { range: 'custom', from, to }, resetScroll: false })
          }}
        >
          <CustomRangeFields from={search.from} to={search.to} />
          {error && (
            <p role="alert" className="text-destructive text-xs">
              {error}
            </p>
          )}
          <Button type="submit" size="sm">
            Apply
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  )
}

function CustomRangeFields(props: { from: number | undefined; to: number | undefined }) {
  // Rendered only once the popover opens, so reading the clock here never reaches the server render.
  const [now] = useState(() => Date.now())
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor="metrics-from">From</Label>
        <Input
          id="metrics-from"
          name="from"
          type="datetime-local"
          defaultValue={toUtcInput(props.from ?? now - 86_400_000)}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="metrics-to">To</Label>
        <Input id="metrics-to" name="to" type="datetime-local" defaultValue={toUtcInput(props.to ?? now)} />
      </div>
    </div>
  )
}
